import {computed} from 'vue';

import type {IPaginationFilters, TId} from '@/assets/core/assets/api/types';
import type {IRequestSuccess} from '@/assets/core/assets/api/types';

import useRequestHook from './requestHook';
import useRequestPaginationHook from './requestPaginationHook';

import type {IQueryObject} from '@/assets/core/assets/helpers/serialize';

export type TApiHook<
  TGetShort = unknown,
  TGet = unknown,
  TCreate = unknown,
  TUpdate = unknown,
  TFilters extends IQueryObject = IQueryObject,
> = ReturnType<typeof useAPIHook<TGetShort, TGet, TCreate, TUpdate, TFilters>>;

const useAPIHook = <
  TGetShort = unknown,
  TGet = unknown,
  TCreate = unknown,
  TUpdate = unknown,
  TFilters extends IQueryObject = IQueryObject,
>(
  baseUrl: URL,
  baseFilters?: TFilters,
  basePagination?: Omit<IPaginationFilters, 'page'>,
) => {
  const request = useRequestHook();
  const requestPagination = useRequestPaginationHook<TGetShort, TFilters>(
    baseUrl,
    baseFilters,
    basePagination,
  );

  const getURL = computed(() => baseUrl);

  const getURLById = (id?: TId) => {
    const url = new URL(baseUrl.toString());

    if (id == null) return url;

    if (url.pathname.includes('{id}'))
      url.pathname.replace('{id}', id.toString());
    else url.pathname += '/' + id.toString();

    return url;
  };

  const clone = () =>
    useAPIHook<TGetShort, TGet, TCreate, TUpdate, TFilters>(
      baseUrl,
      baseFilters,
      basePagination,
    );
  const cloneWithParams = (
    filters?: TFilters,
    pagination?: Omit<IPaginationFilters, 'page'>,
  ) => {
    return useAPIHook<TGetShort, TGet, TCreate, TUpdate, TFilters>(
      baseUrl,
      filters ?? baseFilters,
      pagination ?? basePagination,
    );
  };

  const getIsLoading = computed(
    () => request.isLoading.value || requestPagination.isLoading.value,
  );

  const getOneRequest = async (id?: TId): Promise<TGet> => {
    return await request.get(getURLById(id));
  };

  const createOneRequest = async (payload: TCreate): Promise<TGet> => {
    return await request.post(baseUrl, payload);
  };

  const updateOneRequest = async (
    id?: TId,
    payload?: TUpdate,
  ): Promise<TGet> => {
    return await request.patch(getURLById(id), payload);
  };

  const deleteOneRequest = async (id?: TId): Promise<IRequestSuccess> => {
    return await request.delete(getURLById(id));
  };

  return {
    getRequestLastDetail: request.getRequestLastDetail,

    list: requestPagination,

    getURL,
    getIsLoading,

    getOne: getOneRequest,

    createOne: createOneRequest,
    updateOne: updateOneRequest,
    deleteOne: deleteOneRequest,

    clone,
    cloneWithParams,
  };
};

export default useAPIHook;
